import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import {
  theme,
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useRedeemGiftCardDefaultValues } from '@order/features/gift-card';
import { StripeCustomerSheet } from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import {
  AccountPaymentGiftCardLoading,
  AccountScreenContainer,
  AccountScreenTitle,
  GiftCardAccountSection,
  PaymentAccountSection,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { usePaymentMethods } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentAndGiftCardsScreen = () => {
  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();

  const navigation = useSafeNavigation();
  const expoNavigation = useSafeExpoNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();

  const title = formatMessage(messages.title);
  const containerA11ySelector = useA11ySelector('account.payments.container');
  const paymentMethodContainerA11ySelector = useA11ySelector('account.payments.payment-method-container'); // prettier-ignore

  const routeParams = useSafeRouteParams<{ giftCardId?: string }>();
  const giftCardRedemptionEncodedData = routeParams?.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const redirectToParams = useMemo(
    () => ({ redirectToGiftCardId: routeParams?.giftCardId }),
    [routeParams?.giftCardId],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore
  const isStripeSdkEnabled = useFeatureFlag('CELS-4677-account-stripe-sdk-enabled'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isExpoRouterEnabled) {
      if (router?.canGoBack()) {
        router.back();

        return;
      }

      router?.dismissTo('/account');

      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();

      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: { screen: 'AccountMenu' },
    });
  }, [isExpoRouterEnabled, router, navigation]);

  const setOptions = navigation?.setOptions ?? expoNavigation?.setOptions;

  useLayoutEffect(() => {
    if (!isExpoRouterEnabled) setOptions?.({ headerShown: true });
  }, [setOptions, isExpoRouterEnabled]);

  if (!isLoggedIn) {
    return (
      <SignedOutView
        redirectTo="payment-and-gift-cards"
        redirectToParams={redirectToParams}
        withoutFooter
      />
    );
  }

  if (isStripeSdkEnabled) {
    return (
      <AccountScreenContainer screen="payment-and-gift-cards" {...containerA11ySelector.props}>
        <AccountScreenTitle title={title} />

        <StripeCustomerSheet visible redirectTo={redirectTo} onClose={handleClose} />
      </AccountScreenContainer>
    );
  }

  if (isLoadingPaymentMethods) {
    return <AccountPaymentGiftCardLoading />;
  }

  return (
    <AccountScreenContainer screen="payment-and-gift-cards" {...containerA11ySelector.props}>
      <AccountScreenTitle visibility="COMPACT" title={title} />

      <View style={match([styles.paymentSectionContainerXS, undefined])}>
        <PaymentAccountSection
          paymentMethods={paymentMethods}
          {...paymentMethodContainerA11ySelector.props}
        />
      </View>

      {isInStoreOrdersEnabled ? null : (
        <GiftCardAccountSection
          giftCardCode={defaultRedemptionValues?.giftCardCode}
          giftCardRegistrationCode={defaultRedemptionValues?.giftCardRegistrationCode}
        />
      )}
    </AccountScreenContainer>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const redirectTo = `https://${window.location.host}/account/payment-and-gift-cards`;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payments',
    description: 'Payments Screen | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentSectionContainerXS: {
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['10'],
  },
});
